import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConversationPane } from './ConversationPane';
import { FooterDisclaimer } from './FooterDisclaimer';
import { InputArea } from './InputArea';
import { ArrowDownIcon } from './WidgetIcons';
import { WidgetHeader } from './WidgetHeader';
import styles from './WidgetStyles.module.css';
import type { ChatConfiguration, UserImageUpload } from '../types/chat';
import { useChatSession } from '../hooks/useChatSession';
import { ensureI18nLocaleSafe } from '../localization/i18n';
import { resolveLocale } from '../localization/translations';
import { normalizeChatConfiguration } from '../services/chatConfiguration';
import { useExampleQuestions } from '../hooks/useExampleQuestions';
import type { ExamplePromptsPanelProps } from './ExamplePromptsPanel';
import { MAX_WIDGET_IMAGE_BYTES, MAX_WIDGET_IMAGES } from '../constants/uploads';
import { normalizeImageForUpload } from '../services/imageUploadNormalizer';

export type FloatingOffsets = {
    bottom?: number;
    right?: number;
};

export type WidgetContainerProps = {
    title?: string;
    brandLabel?: string;
    chat?: ChatConfiguration;
    language?: string;
    defaultDocked?: boolean;
    onDockChanged?: (isDocked: boolean) => void;
    onClose?: () => void;
    isVisible?: boolean;
    floatingOffsets?: FloatingOffsets;
    showHeader?: boolean;
    showResponseDuration?: boolean;
    dockingEnabled?: boolean;
    exampleQuestionsCacheTtlMinutes?: number;
    onRuntimeReady?: (runtime: WidgetContainerRuntime | null) => void;
};

export type WidgetContainerRuntime = {
    send: (message: string) => boolean;
};

const DEFAULT_CHAT_ENDPOINT = 'https://product-assist.skf.com/public/assistant/chat';
const defaultBrand = 'SKF';
const mobileViewportMediaQuery = '(max-width: 768px)';
const defaultFloatingOffsets: Required<FloatingOffsets> = {
    bottom: 44,
    right: 44
};

export function WidgetContainer({
    title = 'Product Assistant',
    brandLabel = defaultBrand,
    chat,
    language,
    defaultDocked = false,
    onDockChanged,
    onClose,
    isVisible = true,
    floatingOffsets,
    showHeader = true,
    showResponseDuration = true,
    dockingEnabled = true,
    exampleQuestionsCacheTtlMinutes,
    onRuntimeReady
}: WidgetContainerProps) {
    const { t } = useTranslation();
    const resolvedLocale = useMemo(() => resolveLocale(language), [language]);
    const [activeLocale, setActiveLocale] = useState(resolvedLocale);
    const [showScrollToLatest, setShowScrollToLatest] = useState(false);
    const [isScrollToLatestMounted, setIsScrollToLatestMounted] = useState(false);
    const scrollToLatestRef = useRef<(() => void) | null>(null);
    const scrollToLatestHideTimeoutRef = useRef<number | null>(null);

    useEffect(() => {
        setActiveLocale(resolvedLocale);
    }, [resolvedLocale]);

    useEffect(() => {
        void ensureI18nLocaleSafe(activeLocale);
    }, [activeLocale]);

    useEffect(() => {
        return () => {
            if (scrollToLatestHideTimeoutRef.current !== null) {
                window.clearTimeout(scrollToLatestHideTimeoutRef.current);
            }
        };
    }, []);
    const resolvedChat = useMemo<ChatConfiguration>(() => {
        if (!chat) {
            return { endpoint: DEFAULT_CHAT_ENDPOINT };
        }

        if (typeof chat === 'string') {
            const normalized = chat.trim();
            return normalized.length > 0 ? normalized : { endpoint: DEFAULT_CHAT_ENDPOINT };
        }

        if (!chat.endpoint?.trim()) {
            return {
                ...chat,
                endpoint: DEFAULT_CHAT_ENDPOINT
            };
        }

        return chat;
    }, [chat]);
    const stableChat = useStableChatConfig(resolvedChat);
    const chatConfiguration = useMemo(() => normalizeChatConfiguration(stableChat), [stableChat]);
    const [isDocked, setIsDocked] = useState(defaultDocked);
    const [isCompactViewport, setIsCompactViewport] = useState(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
            return false;
        }

        return window.matchMedia(mobileViewportMediaQuery).matches;
    });
    const [pendingImages, setPendingImages] = useState<UserImageUpload[]>([]);
    const [imageError, setImageError] = useState<string | null>(null);
    const {
        messages,
        draftMessage,
        isStreaming,
        pendingStatus,
        images,
        taskPlan,
        isDebugConsoleEnabled,
        conversationResponseCount,
        setDraftMessage,
        sendMessage,
        sendProgrammaticMessage,
        cancelStreaming,
        resetConversation,
        copyMessage,
        copyConversationJsonResponses,
        canSubmitLatestFeedback,
        isSubmittingFeedback,
        submitLatestFeedback
    } = useChatSession({
        chat,
        configuration: chatConfiguration,
        language: activeLocale,
        onLanguageOverride: setActiveLocale
    });
    const exampleEndpoint = useMemo(() => buildExampleEndpoint(chatConfiguration?.endpoint), [chatConfiguration]);
    const exampleHeaders = useMemo(() => sanitizeHeaders(chatConfiguration?.headers), [chatConfiguration]);
    const exampleQuestionsCacheTtlMs = useMemo(() => {
        if (exampleQuestionsCacheTtlMinutes === undefined) {
            return undefined;
        }

        return exampleQuestionsCacheTtlMinutes * 60_000;
    }, [exampleQuestionsCacheTtlMinutes]);
    const { categories: exampleCategories } = useExampleQuestions({
        endpoint: exampleEndpoint,
        headers: exampleHeaders,
        language: activeLocale,
        cacheTtlMs: exampleQuestionsCacheTtlMs
    });

    const handleClose = useCallback(() => {
        setIsDocked(false);
        onClose?.();
    }, [onClose]);

    const handleExampleQuestionSelected = useCallback(
        (question: string) => {
            const normalized = question?.trim();

            if (!normalized) {
                return;
            }

            setDraftMessage(normalized);
        },
        [setDraftMessage]
    );

    const examplePromptsPanel: ExamplePromptsPanelProps | null = useMemo(() => {
        if (!exampleEndpoint || exampleCategories.length === 0) {
            return null;
        }

        return {
            heading: t('examplePromptsHeading'),
            categories: exampleCategories,
            onQuestionSelected: handleExampleQuestionSelected
        };
    }, [
        exampleEndpoint,
        exampleCategories,
        handleExampleQuestionSelected,
        t
    ]);

    useEffect(() => {
        setIsDocked(defaultDocked);
    }, [defaultDocked]);

    useEffect(() => {
        onDockChanged?.(isDocked);
    }, [isDocked, onDockChanged]);

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
            return;
        }

        const mediaQueryList = window.matchMedia(mobileViewportMediaQuery);
        setIsCompactViewport(mediaQueryList.matches);

        const handleViewportChange = (event: MediaQueryListEvent) => {
            setIsCompactViewport(event.matches);
        };

        if (typeof mediaQueryList.addEventListener === 'function') {
            mediaQueryList.addEventListener('change', handleViewportChange);
            return () => {
                mediaQueryList.removeEventListener('change', handleViewportChange);
            };
        }

        mediaQueryList.addListener(handleViewportChange);
        return () => {
            mediaQueryList.removeListener(handleViewportChange);
        };
    }, []);

    const isInputDisabled = isStreaming;
    const totalImageBytes = useMemo(
        () => pendingImages.reduce((sum, image) => sum + image.sizeBytes, 0),
        [pendingImages]
    );

    const containerClassName = [
        styles.widgetShell,
        isDocked ? styles.widgetShellDocked : '',
        !isVisible ? styles.widgetShellHidden : ''
    ]
        .filter(Boolean)
        .join(' ');

    const resolvedOffsets = {
        bottom: floatingOffsets?.bottom ?? defaultFloatingOffsets.bottom,
        right: floatingOffsets?.right ?? defaultFloatingOffsets.right
    };

    const floatingStyle = !isDocked
        ? isCompactViewport
            ? ({
                  position: 'fixed',
                  top: '0px',
                  right: '0px',
                  bottom: '0px',
                  left: '0px',
                  width: '100vw',
                  maxWidth: '100vw',
                  height: '100dvh',
                  maxHeight: '100dvh',
                  borderRadius: '0px',
                  boxShadow: 'none'
              } as const)
            : ({
                  position: 'fixed',
                  bottom: `${resolvedOffsets.bottom}px`,
                  right: `${resolvedOffsets.right}px`
              } as const)
        : undefined;
    const dockedStyle = isDocked
        ? ({
              width: '100%',
              maxWidth: '100%',
              minWidth: '0',
              height: '100%'
          } as const)
        : undefined;
    const containerStyle = {
        ...(dockedStyle ?? {}),
        ...(floatingStyle ?? {}),
        ...(!isVisible ? { display: 'none' } : {})
    };
    const resolvedContainerStyle = Object.keys(containerStyle).length ? containerStyle : undefined;

    const handleImagesSelected = useCallback(
        async (files: File[]) => {
            if (!files.length || isInputDisabled) {
                return;
            }

            const availableSlots = MAX_WIDGET_IMAGES - pendingImages.length;
            if (availableSlots <= 0) {
                setImageError(t('imageLimitReached'));
                return;
            }

            const sanitized = files.filter(isLikelyImageFile);
            if (!sanitized.length) {
                return;
            }

            const acceptedFiles = sanitized.slice(0, availableSlots);
            const uploads: UserImageUpload[] = [];
            let totalBytes = totalImageBytes;
            let limitReached = false;
            let readError = false;

            for (const file of acceptedFiles) {
                if (file.size === 0) {
                    continue;
                }

                const availableBytes = MAX_WIDGET_IMAGE_BYTES - totalBytes;
                if (availableBytes <= 0) {
                    limitReached = true;
                    break;
                }

                try {
                    const normalizedUpload = await normalizeImageForUpload(file, availableBytes);
                    if (!normalizedUpload) {
                        limitReached = true;
                        continue;
                    }

                    uploads.push({
                        id: createClientId('image'),
                        fileName: normalizedUpload.fileName || `image-${uploads.length + 1}.png`,
                        base64: normalizedUpload.base64,
                        mimeType: normalizedUpload.mimeType || 'image/png',
                        sizeBytes: normalizedUpload.sizeBytes
                    });
                    totalBytes += normalizedUpload.sizeBytes;
                } catch {
                    readError = true;
                    break;
                }
            }

            if (uploads.length) {
                setPendingImages((previous) => [...previous, ...uploads]);
            }

            if (readError) {
                setImageError(t('imageReadError'));
                return;
            }

            if (limitReached) {
                setImageError(t('imageLimitReached'));
                return;
            }

            setImageError(null);
        },
        [isInputDisabled, pendingImages.length, t, totalImageBytes]
    );

    const handleRemoveImage = useCallback((imageId: string) => {
        setPendingImages((previous) => previous.filter((image) => image.id !== imageId));
    }, []);

    useEffect(() => {
        if (imageError !== t('imageLimitReached')) {
            return;
        }

        if (pendingImages.length <= MAX_WIDGET_IMAGES && totalImageBytes <= MAX_WIDGET_IMAGE_BYTES) {
            setImageError(null);
        }
    }, [imageError, pendingImages.length, t, totalImageBytes]);

    const handleSendWithImages = useCallback(async () => {
        const imagesToSend = pendingImages;
        setPendingImages([]);
        setImageError(null);
        await sendMessage(imagesToSend);
    }, [pendingImages, sendMessage]);

    const handleResetConversation = useCallback(() => {
        resetConversation();
        setPendingImages([]);
        setImageError(null);
    }, [resetConversation]);

    const dockingAllowed = dockingEnabled !== false;

    const handleToggleDock = useCallback(() => {
        if (!dockingAllowed) {
            return;
        }

        setIsDocked((previous) => !previous);
    }, [dockingAllowed]);

    const handleScrollIndicatorVisibilityChange = useCallback((nextIsVisible: boolean) => {
        if (scrollToLatestHideTimeoutRef.current !== null) {
            window.clearTimeout(scrollToLatestHideTimeoutRef.current);
            scrollToLatestHideTimeoutRef.current = null;
        }

        if (nextIsVisible) {
            setIsScrollToLatestMounted(true);
            setShowScrollToLatest(true);
            return;
        }

        setShowScrollToLatest(false);
        scrollToLatestHideTimeoutRef.current = window.setTimeout(() => {
            setIsScrollToLatestMounted(false);
            scrollToLatestHideTimeoutRef.current = null;
        }, 250);
    }, []);

    const handleScrollToLatestChange = useCallback((scrollToLatest: (() => void) | null) => {
        scrollToLatestRef.current = scrollToLatest;
    }, []);

    const handleScrollToLatest = useCallback(() => {
        scrollToLatestRef.current?.();
    }, []);

    useEffect(() => {
        if (!onRuntimeReady) {
            return;
        }

        onRuntimeReady({
            send: (message: string) => {
                return sendProgrammaticMessage(message);
            }
        });

        return () => {
            onRuntimeReady(null);
        };
    }, [onRuntimeReady, sendProgrammaticMessage]);

    return (
        <div
            className={containerClassName}
            part="widget-shell"
            data-skf-part="widget-shell"
            aria-label={`${title} dockable widget`}
            aria-hidden={!isVisible}
            style={resolvedContainerStyle}
        >
            {showHeader ? (
                <WidgetHeader
                    brandLabel={brandLabel}
                    isDocked={isDocked}
                    dockingEnabled={dockingAllowed}
                    onToggleDock={handleToggleDock}
                    onClose={handleClose}
                />
            ) : null}
            <div className={styles.widgetBody}>
                <ConversationPane
                    messages={messages}
                    isStreaming={isStreaming}
                    pendingStatus={pendingStatus}
                    images={images}
                    onCopyMessage={copyMessage}
                    examplePrompts={examplePromptsPanel}
                    showDuration={showResponseDuration}
                    canSubmitLatestFeedback={canSubmitLatestFeedback}
                    isSubmittingFeedback={isSubmittingFeedback}
                    onSubmitFeedback={submitLatestFeedback}
                    isVisible={isVisible}
                    onScrollIndicatorVisibilityChange={handleScrollIndicatorVisibilityChange}
                    onScrollToLatestChange={handleScrollToLatestChange}
                />
                <div className={styles.composerRegion}>
                    {isScrollToLatestMounted ? (
                        <div className={styles.scrollToBottomSeam}>
                            <div className={styles.scrollToBottomAnchor}>
                                <button
                                    className={`${styles.scrollToBottomButton} ${
                                        showScrollToLatest ? styles.scrollToBottomButtonVisible : styles.scrollToBottomButtonHidden
                                    }`}
                                    type="button"
                                    aria-label={t('scrollToLatest')}
                                    title={t('scrollToLatest')}
                                    onClick={handleScrollToLatest}
                                >
                                    <ArrowDownIcon />
                                </button>
                            </div>
                        </div>
                    ) : null}
                    <InputArea
                        textareaId="product-assistant-input"
                        value={draftMessage}
                        onChange={setDraftMessage}
                        onSend={handleSendWithImages}
                        onCancel={cancelStreaming}
                        onReset={handleResetConversation}
                        isStreaming={isStreaming}
                        disabled={isInputDisabled}
                        images={pendingImages}
                        onAttachImages={handleImagesSelected}
                        onRemoveImage={handleRemoveImage}
                        imageError={imageError}
                        showMediaControls={true}
                        taskPlan={taskPlan}
                        showDebugCopyButton={isDebugConsoleEnabled}
                        onCopyConversationResponses={copyConversationJsonResponses}
                        hasConversationResponses={conversationResponseCount > 0}
                    />
                </div>
            </div>
            <FooterDisclaimer />
        </div>
    );
}

export type ProductAssistantWidgetProps = WidgetContainerProps;
export const ProductAssistantWidget = WidgetContainer;

export default WidgetContainer;

function buildExampleEndpoint(endpoint?: string | null): string | undefined {
    if (!endpoint || !endpoint.trim()) {
        return undefined;
    }

    try {
        const url = new URL('./example-questions', endpoint);
        return url.toString();
    } catch {
        return undefined;
    }
}

function sanitizeHeaders(headers?: Record<string, string | undefined> | null): Record<string, string> | null {
    if (!headers) {
        return null;
    }

    const normalized: Record<string, string> = {};

    Object.entries(headers).forEach(([key, value]) => {
        if (value) {
            normalized[key] = value;
        }
    });

    return Object.keys(normalized).length > 0 ? normalized : null;
}

function createClientId(prefix: string): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return `${prefix}-${crypto.randomUUID()}`;
    }

    return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function useStableChatConfig(chat?: ChatConfiguration): ChatConfiguration | undefined {
    const previousRef = useRef<ChatConfiguration | undefined>();

    if (areChatConfigurationsEqual(previousRef.current, chat)) {
        return previousRef.current;
    }

    previousRef.current = chat;
    return chat;
}

function areChatConfigurationsEqual(a?: ChatConfiguration, b?: ChatConfiguration): boolean {
    if (a === b) {
        return true;
    }

    if (!a || !b) {
        return !a && !b;
    }

    if (typeof a === 'string' || typeof b === 'string') {
        const left = typeof a === 'string' ? a : undefined;
        const right = typeof b === 'string' ? b : undefined;
        return normalizeConfigValue(left) === normalizeConfigValue(right);
    }

    return (
        normalizeConfigValue(a.endpoint) === normalizeConfigValue(b.endpoint) &&
        normalizeConfigValue(a.agentId) === normalizeConfigValue(b.agentId) &&
        a.getAuthorizationToken === b.getAuthorizationToken &&
        a.onEnrichContext === b.onEnrichContext &&
        areHeaderMapsEqual(a.headers, b.headers)
    );
}

function areHeaderMapsEqual(
    a?: Record<string, string | undefined>,
    b?: Record<string, string | undefined>
): boolean {
    const aEntries = normalizeHeadersForComparison(a);
    const bEntries = normalizeHeadersForComparison(b);

    if (aEntries.length !== bEntries.length) {
        return false;
    }

    for (let i = 0; i < aEntries.length; i += 1) {
        const [aKey, aValue] = aEntries[i];
        const [bKey, bValue] = bEntries[i];

        if (aKey !== bKey || aValue !== bValue) {
            return false;
        }
    }

    return true;
}

function normalizeHeadersForComparison(
    headers?: Record<string, string | undefined>
): Array<[string, string]> {
    if (!headers) {
        return [];
    }

    const entries: Array<[string, string]> = Object.entries(headers)
        .filter(([, value]) => Boolean(value && value.trim()))
        .map(([key, value]) => [key.trim().toLowerCase(), (value ?? '').trim()]);

    return entries.sort((a, b) => a[0].localeCompare(b[0]));
}

function normalizeConfigValue(value?: string): string {
    return value?.trim() ?? '';
}

function isLikelyImageFile(file: File): boolean {
    if (file.type?.toLowerCase().startsWith('image/')) {
        return true;
    }

    const lowerCaseName = (file.name ?? '').trim().toLowerCase();
    if (!lowerCaseName) {
        return false;
    }

    return (
        lowerCaseName.endsWith('.jpg') ||
        lowerCaseName.endsWith('.jpeg') ||
        lowerCaseName.endsWith('.png') ||
        lowerCaseName.endsWith('.webp') ||
        lowerCaseName.endsWith('.bmp') ||
        lowerCaseName.endsWith('.gif') ||
        lowerCaseName.endsWith('.heic') ||
        lowerCaseName.endsWith('.heif') ||
        lowerCaseName.endsWith('.avif')
    );
}
